'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { getVariantPrice } from '@/lib/productUtils';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Initialize cart from storage
  useEffect(() => {
    const initializeCart = () => {
      try {
        // Try localStorage first
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          //console.log('Loading cart from localStorage:', parsedCart);
          setCart(parsedCart);
          return;
        }

        // Fallback to sessionStorage
        const sessionCart = sessionStorage.getItem('cart');
        if (sessionCart) {
          const parsedSessionCart = JSON.parse(sessionCart);
          //console.log('Loading cart from sessionStorage:', parsedSessionCart);
          setCart(parsedSessionCart);
        }
      } catch (error) {
        console.error('Failed to load cart from storage:', error);
        setCart([]);
      }
    };

    initializeCart();
  }, []);

  // Save cart to both storages
  useEffect(() => {
    const saveCart = () => {
      try {
        const cartString = JSON.stringify(cart);
        localStorage.setItem('cart', cartString);
        sessionStorage.setItem('cart', cartString);
        //console.log('Saved cart to storage:', cart);
      } catch (error) {
        console.error('Failed to save cart to storage:', error);
      }
    };

    if (cart.length > 0) {
      saveCart();
    }
  }, [cart]);

  const addToCart = (product, selectedVariants = {}) => {
    setCart((prevCart) => {
      // Check if the same product with the same variants already exists in cart
      const existingItemIndex = prevCart.findIndex(item =>
        item.productId === product._id &&
        JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants)
      );

      if (existingItemIndex !== -1) {
        // If item exists, increment its quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + 1
        };
       // console.log('Incrementing quantity for existing item:', updatedCart[existingItemIndex]);
        return updatedCart;
      }

      // If item doesn't exist, create a new cart item
      const uniqueId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const variantPrice = getVariantPrice(product, selectedVariants);
      const newItem = {
        cartItemId: uniqueId,
        productId: product._id,
        name: product.name,
        price: variantPrice, // Use the variant price
        quantity: 1,
        selectedVariants: { ...selectedVariants },
        imageUrl: product.imageUrl,
        salePrice: product.salePrice,
        saleStartDate: product.saleStartDate,
        saleEndDate: product.saleEndDate,
        // Store the original product data to allow recalculation if needed
        productData: {
          variants: product.variants,
          variantPrices: product.variantPrices,
          variations: product.variations
        }
      };

     // console.log('Adding new item to cart:', newItem);
      return [...prevCart, newItem];
    });

    // Open cart
    setIsCartOpen(true);
  };

  const removeFromCart = (cartItemId) => {
    setCart((prevCart) => {
      const updatedCart = prevCart.filter(item => item.cartItemId !== cartItemId);
      //console.log('Removing item from cart:', cartItemId);
     // console.log('Updated cart:', updatedCart);
      return updatedCart;
    });
  };

  const updateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;

    setCart((prevCart) => {
      const updatedCart = prevCart.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: newQuantity }
          : item
      );
      //console.log('Updating quantity for item:', cartItemId);
      //console.log('Updated cart:', updatedCart);
      return updatedCart;
    });
  };

  const clearCart = () => {
    console.log('Clearing cart');
    setCart([]);
    localStorage.removeItem('cart');
    sessionStorage.removeItem('cart');
  };

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      // Use the stored price which should be the correct variant price
      const itemPrice = item.salePrice &&
        item.saleStartDate &&
        item.saleEndDate &&
        new Date() >= new Date(item.saleStartDate) &&
        new Date() <= new Date(item.saleEndDate)
          ? item.salePrice
          : item.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Add a function to sync cart with storage
  const syncCartWithStorage = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCart(parsedCart);
      }
    } catch (error) {
      console.error('Failed to sync cart with storage:', error);
    }
  };

  // Add event listener for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart') {
        syncCartWithStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const getCartItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      isCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      toggleCart,
      getCartTotal,
      syncCartWithStorage,
      getCartItemsCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
